/**
 * Tiny scroll-reveal hook. Returns a ref to attach to any element and
 * a `shown` flag that flips true the first time the element crosses
 * the viewport threshold. Disconnects after the first reveal.
 *
 * Pair with the .gv-reveal CSS rule so the element fades up from
 * `translateY(16px)` → `translateY(0)`.
 */

import { useEffect, useRef, useState } from 'react';

export function useReveal<T extends HTMLElement>(threshold = 0.25) {
  const ref = useRef<T | null>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!ref.current || shown) return;
    if (typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      entries => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShown(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold },
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [shown, threshold]);

  return { ref, shown };
}
